import React from 'react';
import BaseScadaNode from './BaseScadaNode';
import { GateIndicator } from './RibbonBlenderNode';
import { isGateOpen } from './signal_utils';

export function BucketElevatorSVG({ config = {}, width = 120, height = 50, liveValues = {} }) {
    const threshold = parseFloat(config.threshold ?? 0.5);
    const stateVal = liveValues.state;
    const running = stateVal != null && parseFloat(stateVal) >= threshold;
    const color = running ? (config.run_color || '#22C55E') : (config.stop_color || '#64748B');

    const inletCount  = parseInt(config.inlet_count  ?? 1);
    const outletCount = parseInt(config.outlet_count ?? 1);
    const outletSensorType = config.outlet_sensor_type || 'single';
    const outletOpenLevel  = config.outlet_open_level  || 'high';
    const gateW = 10;

    const cy     = height / 2;
    const pulleyR = 10;
    const bodyH  = 16;
    const bodyY  = cy - bodyH / 2;
    const leftCX = pulleyR + 4;
    const rightCX = width - pulleyR - 4;
    const beltTop = bodyY + 2;
    const beltBot = bodyY + bodyH - 2;

    const numBuckets = 5;
    const bucketXs = Array.from({ length: numBuckets }, (_, i) =>
        leftCX + (rightCX - leftCX) * (i / (numBuckets - 1))
    );

    // Inlets evenly spread between pulleys along top
    const inletXs = Array.from({ length: inletCount }, (_, i) => {
        const pct = inletCount === 1 ? 0.5 : i / (inletCount - 1);
        return leftCX + pct * (rightCX - leftCX);
    });

    // Outlets evenly spread along full width at bottom
    const outletXs = Array.from({ length: outletCount }, (_, i) => {
        const pct = outletCount === 1 ? 0.5 : i / (outletCount - 1);
        return 8 + pct * (width - 16);
    });

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Left pulley */}
            <ellipse cx={leftCX} cy={cy} rx={6} ry={pulleyR} fill="#1E293B" stroke={color} strokeWidth={2} />
            {/* Right pulley */}
            <ellipse cx={rightCX} cy={cy} rx={6} ry={pulleyR} fill="#1E293B" stroke={color} strokeWidth={2} />
            {/* Casing body */}
            <rect x={leftCX} y={bodyY} width={rightCX - leftCX} height={bodyH} fill="#1E293B" stroke={color} strokeWidth={1.5} />
            {/* Belt lines */}
            <line x1={leftCX} y1={beltTop} x2={rightCX} y2={beltTop} stroke={color} strokeWidth={1} opacity={0.6} />
            <line x1={leftCX} y1={beltBot} x2={rightCX} y2={beltBot} stroke={color} strokeWidth={1} opacity={0.6} />
            {/* Buckets */}
            {bucketXs.map((x, i) => (
                <rect key={i} x={x - 4} y={beltBot - 1} width={8} height={6} rx={1} fill={color} opacity={0.8} />
            ))}
            {/* Direction arrow */}
            <text x={width / 2 - 4} y={cy + 4} fill={color} fontSize={9} opacity={0.8}>{running ? '→' : '—'}</text>

            {/* Inlet stubs (top) */}
            {inletXs.map((ix, i) => (
                <g key={`in_${i}`}>
                    <line x1={ix} y1={bodyY} x2={ix} y2={2} stroke={color} strokeWidth={1.5} opacity={0.8} />
                    <rect x={ix - 5} y={0} width={10} height={4} rx={1} fill="#334155" stroke={color} strokeWidth={1} />
                </g>
            ))}

            {/* Outlet stubs (bottom) */}
            {outletXs.map((ox, i) => {
                const signalKey = outletCount === 1 ? 'outlet_1' : `outlet_${i + 1}`;
                const closeKey  = signalKey + '_close';
                const gateOpen  = isGateOpen(liveValues, signalKey, closeKey, outletSensorType, outletOpenLevel, threshold);
                return (
                    <g key={`out_${i}`}>
                        <line x1={ox} y1={bodyY + bodyH} x2={ox} y2={gateOpen !== null ? height - gateW - 2 : height - 2}
                            stroke={color} strokeWidth={1.5} opacity={0.8} />
                        <GateIndicator
                            x={ox - gateW / 2}
                            y={height - gateW - 2}
                            width={gateW}
                            open={gateOpen}
                            color_open={config.on_color}
                            color_closed={config.off_color}
                        />
                    </g>
                );
            })}
        </svg>
    );
}

export default function BucketElevatorNode(props) {
    const { data } = props;
    return (
        <BaseScadaNode {...props}>
            <BucketElevatorSVG
                config={data?.config}
                width={data?.width || 120}
                height={data?.height || 50}
                liveValues={data?.liveValues || {}}
            />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
